import axios from 'axios';
import { apiUri } from '../config';
import { useAlert } from 'context/alertContext';
import { useNavigate } from 'react-router-dom';

const UseApi = () => {
  const { setErrorMessage, setSuccessMessage } = useAlert();
  const navigate = useNavigate();

  const unauthorize = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('admin');
    navigate('/auth/sign-in');
  };

  const catchError = async (error: any, request: any) => {
    let accessToken = localStorage.getItem('accessToken');
    let refreshToken = localStorage.getItem('refreshToken');
    const { response } = error;
    if (response?.status === 401 && refreshToken) {
      // Access token expired, try to refresh
      try {
        const refreshResponse = await axios.post(`${apiUri}/token/refresh`, {
          token: refreshToken,
        });
        if (refreshResponse.status === 200) {
          accessToken = refreshResponse.data.accessToken;
          localStorage.setItem('accessToken', accessToken);
          return request();
        } else {
          unauthorize();
        }
      } catch (e) {
        unauthorize();
      }
    } else if (response?.status === 401) {
      unauthorize();
    } else {
      setErrorMessage(response?.data?.error || 'API Error');
          console.error('Unknown API error:', error); // Log for debug
    }
  };

  const requestWithRefresh = async (request: () => Promise<any>) => {
    let response;
    try {
      response = await request();
      if (response?.status === 201) {
        setSuccessMessage('Added Successfully');
      } else if (response?.status === 211) {
        setSuccessMessage('Deleted Successfully');
      } else if (response?.status === 212) {
        setSuccessMessage('Updated Successfully');
      } else if (response?.status === 213) {
        setSuccessMessage('Archived Successfully');
      } else if (response?.status === 202) {
        setSuccessMessage('Done Successfully');
      }
    } catch (error: any) {
      catchError(error, request);
    }

    return response;
  };

  const getApi = async (url: string): Promise<any> => {
    try {
      return await requestWithRefresh(() =>
        axios.get(`${apiUri}/${url}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }),
      );
    } catch (error: any) {
      return catchError(error, getApi(url));
    }
  };

  const postApi = async (url: string, data: any): Promise<any> => {
    try {
      return await requestWithRefresh(() =>
        axios.post(`${apiUri}/${url}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }),
      );
    } catch (error: any) {
      catchError(error, postApi(url, data));
    }
  };

  const patchApi = async (url: string, data: any): Promise<any> => {
    try {
      return await requestWithRefresh(() =>
        axios.patch(`${apiUri}/${url}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }),
      );
    } catch (error: any) {
      catchError(error, patchApi(url, data));
    }
  };

  const deleteApi = async (url: string): Promise<any> => {
    try {
      return await requestWithRefresh(() =>
        axios.delete(`${apiUri}/${url}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }),
      );
    } catch (error: any) {
      catchError(error, deleteApi(url));
    }
  };

  return { getApi, postApi, patchApi, deleteApi };
};

export default UseApi;
